import type { GrauProcesso, ResumoProcesso } from "@consulta/core";
import { useState } from "react";
import { data, dataHora, duracao, numero as formatarNumero } from "../formato.js";

/** Quantos movimentos mostrar antes de pedir para expandir. */
const MOVIMENTOS_INICIAIS = 15;

function Dado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="dado">
      <div className="dado-rotulo">{rotulo}</div>
      <div className="dado-valor">{children ?? "—"}</div>
    </div>
  );
}

function LinhaDoTempo({ grau }: { grau: GrauProcesso }) {
  const [expandido, setExpandido] = useState(false);
  const visiveis = expandido ? grau.movimentos : grau.movimentos.slice(0, MOVIMENTOS_INICIAIS);
  const ocultos = grau.movimentos.length - visiveis.length;

  if (grau.movimentos.length === 0) {
    return <p className="dica">Este grau não tem movimentos registrados na base do DataJud.</p>;
  }

  return (
    <>
      <ol className="linha-tempo">
        {visiveis.map((mov, i) => (
          <li className="movimento" key={`${mov.codigo}-${mov.dataHora}-${i}`}>
            <div className="movimento-data">{dataHora(mov.dataHora)}</div>
            <div>
              <div className="movimento-nome">
                {mov.nome} {mov.codigo && <span className="movimento-codigo">#{mov.codigo}</span>}
              </div>
              {mov.complementos.map((c) => (
                <div className="movimento-complemento" key={c}>
                  {c}
                </div>
              ))}
            </div>
          </li>
        ))}
      </ol>
      {ocultos > 0 && (
        <button type="button" className="botao-discreto" onClick={() => setExpandido(true)}>
          Mostrar os outros {formatarNumero(ocultos)} movimentos
        </button>
      )}
      {expandido && grau.movimentos.length > MOVIMENTOS_INICIAIS && (
        <button type="button" className="botao-discreto" onClick={() => setExpandido(false)}>
          Mostrar menos
        </button>
      )}
    </>
  );
}

export function ResumoProcessoView({ resumo }: { resumo: ResumoProcesso }) {
  const [grauAtivo, setGrauAtivo] = useState(0);
  const grau = resumo.graus[grauAtivo] ?? resumo.principal;

  if (!grau) return null;

  return (
    <div className="pilha">
      <section className="cartao">
        <div className="cartao-cabecalho">
          <div>
            <div className="processo-numero">{resumo.numeroFormatado}</div>
            <p className="dica">{resumo.tribunalNome ?? resumo.tribunal}</p>
          </div>
          <div className="linha">
            <span className="etiqueta etiqueta-marca">{resumo.tribunal}</span>
            <span className="etiqueta">{grau.grauDescricao}</span>
            {grau.nivelSigilo !== null && grau.nivelSigilo > 0 && (
              <span className="etiqueta etiqueta-alerta">{grau.nivelSigiloDescricao}</span>
            )}
          </div>
        </div>

        <div className="grade-dados">
          <Dado rotulo="Classe">{grau.classe?.nome}</Dado>
          <Dado rotulo="Órgão julgador">{grau.orgaoJulgador?.nome}</Dado>
          <Dado rotulo="Ajuizamento">{data(resumo.dataAjuizamento)}</Dado>
          <Dado rotulo="Último movimento">{data(resumo.dataUltimoMovimento)}</Dado>
          <Dado rotulo="Em tramitação há">{duracao(resumo.diasEmTramitacao)}</Dado>
          <Dado rotulo="Movimentos">{formatarNumero(resumo.totalMovimentos)}</Dado>
          <Dado rotulo="Base atualizada em">{data(resumo.dataUltimaAtualizacao)}</Dado>
          <Dado rotulo="Assuntos">
            {grau.assuntos.length > 0 ? grau.assuntos.map((a) => a.nome).join("; ") : null}
          </Dado>
          <Dado rotulo="Sistema">{grau.sistema?.nome}</Dado>
          <Dado rotulo="Formato">{grau.formato?.nome}</Dado>
        </div>
      </section>

      <section className="cartao">
        <div className="cartao-cabecalho">
          <h3 style={{ fontSize: 15 }}>
            Movimentações {grau.totalMovimentos > 0 && `(${formatarNumero(grau.totalMovimentos)})`}
          </h3>
          {resumo.graus.length > 1 && (
            <div className="abas-grau" role="group" aria-label="Grau de jurisdição">
              {resumo.graus.map((g, i) => (
                <button
                  type="button"
                  key={g.id || g.grau}
                  className="aba-grau"
                  aria-pressed={i === grauAtivo}
                  onClick={() => setGrauAtivo(i)}
                >
                  {g.grauDescricao}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="cartao-corpo">
          <LinhaDoTempo grau={grau} />
        </div>
      </section>
    </div>
  );
}
